<template>
  <v-container fluid class="guestbook-test-page py-8">
    <v-row justify="center">
      <v-col cols="12" md="9" lg="7" xl="5">
        <v-card class="pa-2 pa-sm-5" rounded="lg">
          <v-card-title class="d-flex align-center">
            <v-icon color="teal" class="mr-3">mdi-book-open-page-variant-outline</v-icon>
            Gästebuch-Testeintrag
          </v-card-title>

          <v-card-subtitle>
            Erstellt einen Eintrag für dein aktuell ausgewähltes Studio und aktualisiert den Arbeitskalender per WebSocket.
          </v-card-subtitle>

          <v-card-text>
            <v-alert type="info" text dense class="mb-6">
              Temporäre interne Testseite. Die Backend-Route ist auf den Live-Domains gesperrt.
            </v-alert>

            <v-form ref="form" v-model="valid" @submit.prevent="createEntry">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field v-model.trim="form.firstName" label="Vorname" outlined dense :rules="requiredRules" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model.trim="form.lastName" label="Nachname" outlined dense :rules="requiredRules" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model.trim="form.email" label="E-Mail" type="email" outlined dense :rules="emailRules" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model.trim="form.phone" label="Telefon" outlined dense :rules="requiredRules" />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="form.referralSource"
                    :items="referralSources"
                    label="Aufmerksam geworden durch"
                    outlined
                    dense
                    :rules="requiredRules" />
                </v-col>
                <v-col v-if="form.referralSource === 'Andere'" cols="12">
                  <v-text-field v-model.trim="form.referralSourceOther" label="Andere Quelle" outlined dense :rules="requiredRules" />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="form.goals"
                    :items="goalOptions"
                    item-text="text"
                    item-value="value"
                    label="Trainingsziele"
                    multiple
                    chips
                    deletable-chips
                    outlined
                    :rules="goalRules" />
                </v-col>
                <v-col v-if="form.goals.includes('other')" cols="12">
                  <v-text-field v-model.trim="form.goalOther" label="Anderes Trainingsziel" outlined dense :rules="requiredRules" />
                </v-col>
              </v-row>

              <v-alert v-if="errorMessage" type="error" text dense>{{ errorMessage }}</v-alert>
              <v-alert v-if="createdEntry" type="success" text dense>
                <strong>{{ createdEntry.name }} {{ createdEntry.surname }}</strong> wurde erstellt.
                <div class="caption mt-1">{{ createdLabel }} · ID {{ createdEntry._id }}</div>
              </v-alert>

              <div class="d-flex flex-wrap justify-end mt-3" style="gap: 10px">
                <v-btn text :disabled="loading" @click="newTestData">Neue Testdaten</v-btn>
                <v-btn v-if="createdEntry" outlined color="teal" @click="openCalendar">Zum Arbeitskalender</v-btn>
                <v-btn color="teal" dark type="submit" :loading="loading" :disabled="!valid">Testeintrag erstellen</v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/api"

const referralSources = [
  "Messe / Ausstellung",
  "Google Suche",
  "Google Werbung",
  "Facebook",
  "Instagram",
  "Tik Tok",
  "Empfehlung",
  "Zeitung",
  "Flyer",
  "Andere",
  "Bereits bekannt",
  "Aussenwerbung",
]

const goalOptions = [
  ["weight_loss", "Abnehmen"],
  ["muscle_gain", "Muskelaufbau"],
  ["toning", "Straffen"],
  ["endurance", "Ausdauer verbessern"],
  ["back_strength", "Rücken kräftigen"],
  ["pain_relief", "Schmerzen beseitigen"],
  ["movement", "Bewegungsdrang ausleben"],
  ["mobility", "Beweglichkeit verbessern"],
  ["coordination", "Koordination verbessern"],
  ["health", "Gesund bleiben"],
  ["posture", "Haltung verbessern"],
  ["rehabilitation", "Rehabilitation"],
  ["competition", "Wettkampfvorbereitung"],
  ["other", "Anderes Ziel"],
].map(([value, text]) => ({ value, text }))

function makeTestForm() {
  const testId = Date.now().toString()
  return {
    firstName: "Test",
    lastName: `Gast ${testId.slice(-6)}`,
    email: `gaestebuch-test+${testId}@example.com`,
    phone: "+41 79 000 00 00",
    referralSource: "Google Suche",
    referralSourceOther: "",
    goals: ["health", "back_strength"],
    goalOther: "",
  }
}

export default {
  name: "GuestbookTestPage",
  data() {
    return {
      valid: false,
      loading: false,
      errorMessage: "",
      createdEntry: null,
      form: makeTestForm(),
      referralSources,
      goalOptions,
      requiredRules: [(value) => Boolean(String(value || "").trim()) || "Pflichtfeld"],
      emailRules: [
        (value) => Boolean(String(value || "").trim()) || "Pflichtfeld",
        (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "")) || "Ungültige E-Mail-Adresse",
      ],
      goalRules: [(value) => (Array.isArray(value) && value.length > 0) || "Mindestens ein Trainingsziel auswählen"],
    }
  },
  computed: {
    createdLabel() {
      const submittedAt = this.createdEntry && this.createdEntry.walkInCalendar && this.createdEntry.walkInCalendar.submittedAt
      return submittedAt ? new Date(submittedAt).toLocaleString("de-CH") : ""
    },
  },
  methods: {
    async createEntry() {
      if (!this.$refs.form.validate()) return
      this.loading = true
      this.errorMessage = ""
      this.createdEntry = null
      try {
        const result = await api.createGuestbookTestEntry({ ...this.form, goals: [...this.form.goals] })
        this.createdEntry = result.entry
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "Der Testeintrag konnte nicht erstellt werden."
      } finally {
        this.loading = false
      }
    },
    newTestData() {
      this.form = makeTestForm()
      this.errorMessage = ""
      this.createdEntry = null
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openCalendar() {
      this.$router.push({ name: "workCalendar" })
    },
  },
}
</script>

<style scoped>
.guestbook-test-page {
  min-height: 100%;
  background: #f4f7f6;
}
</style>
